import { IVetAttendanceForm } from "@/fluxvet/contexts/VetAttendanceContext";
import dayjs from "dayjs";

/**
 * Tipo do item de resposta do QuestionnaireResponse FHIR R5
 */
interface QuestionnaireResponseItem {
  linkId: string;
  text?: string;
  answer?: Array<{
    valueBoolean?: boolean;
    valueDecimal?: number;
    valueInteger?: number;
    valueDate?: string;
    valueDateTime?: string;
    valueTime?: string;
    valueString?: string;
    valueUri?: string;
    valueCoding?: {
      system?: string;
      code?: string;
      display?: string;
    };
    valueQuantity?: {
      value?: number;
      unit?: string;
      system?: string;
      code?: string;
    };
    item?: QuestionnaireResponseItem[];
  }>;
  item?: QuestionnaireResponseItem[];
}

/**
 * Interface do QuestionnaireResponse FHIR R5
 */
export interface QuestionnaireResponse {
  resourceType: "QuestionnaireResponse";
  id?: string;
  questionnaire: string; // Canonical URL do Questionnaire
  status:
    | "in-progress"
    | "completed"
    | "amended"
    | "entered-in-error"
    | "stopped";
  subject?: {
    reference: string; // Referência ao Patient
    display?: string;
  };
  encounter?: {
    reference: string; // Referência ao Encounter (consulta)
  };
  authored?: string; // dateTime
  author?: {
    reference: string; // Referência ao Practitioner/veterinário
    display?: string;
  };
  source?: {
    reference: string; // Quem forneceu as informações (tutor)
  };
  item?: QuestionnaireResponseItem[];
}

/**
 * Converte os dados do formulário de atendimento para QuestionnaireResponse FHIR R5
 *
 * @param formData - Dados do formulário de atendimento
 * @param patientReference - Referência ao Patient (ex: "Patient/123")
 * @param practitionerReference - Referência ao Practitioner (ex: "Practitioner/456")
 * @param encounterId - ID do Encounter (opcional)
 * @returns QuestionnaireResponse FHIR R5
 */
export function convertToQuestionnaireResponse(
  formData: IVetAttendanceForm,
  patientReference: string,
  practitionerReference?: string,
  practitionerDisplay?: string,
  encounterId?: string
): QuestionnaireResponse {
  const items: QuestionnaireResponseItem[] = [];

  // =========================================================================
  // Grupo 1: Anamnese e Histórico Clínico
  // =========================================================================
  const anamneseItems: QuestionnaireResponseItem[] = [];

  if (formData.mainComplaint) {
    anamneseItems.push({
      linkId: "1.1",
      text: "Queixa Principal",
      answer: [{ valueString: formData.mainComplaint }],
    });
  }

  if (formData.anamnesis) {
    anamneseItems.push({
      linkId: "1.2",
      text: "Anamnese Detalhada",
      answer: [{ valueString: formData.anamnesis }],
    });
  }

  if (formData.continuousMedication) {
    anamneseItems.push({
      linkId: "1.3",
      text: "Uso de Medicação Contínua",
      answer: [{ valueString: formData.continuousMedication }],
    });
  }

  if (formData.recentExams) {
    anamneseItems.push({
      linkId: "1.4",
      text: "Exames Recentes",
      answer: [{ valueString: formData.recentExams }],
    });
  }

  if (formData.streetAccess !== undefined) {
    anamneseItems.push({
      linkId: "1.5",
      text: "Acesso à Rua",
      answer: [{ valueBoolean: formData.streetAccess }],
    });
  }

  if (formData.streetAccessDescription) {
    anamneseItems.push({
      linkId: "1.5.1",
      text: "Descrição do Acesso à Rua",
      answer: [{ valueString: formData.streetAccessDescription }],
    });
  }

  if (formData.feeding) {
    anamneseItems.push({
      linkId: "1.6",
      text: "Alimentação",
      answer: [{ valueString: formData.feeding }],
    });
  }

  if (formData.vaccination !== undefined) {
    anamneseItems.push({
      linkId: "1.7",
      text: "Vacinação",
      answer: [{ valueBoolean: formData.vaccination }],
    });
  }

  if (formData.vaccinationDescription) {
    anamneseItems.push({
      linkId: "1.7.1",
      text: "Descrição da Vacinação",
      answer: [{ valueString: formData.vaccinationDescription }],
    });
  }

  if (formData.ectoparasiteControl !== undefined) {
    anamneseItems.push({
      linkId: "1.8",
      text: "Controle de Ectoparasitas",
      answer: [{ valueBoolean: formData.ectoparasiteControl }],
    });
  }

  if (formData.ectoparasiteControlDescription) {
    anamneseItems.push({
      linkId: "1.8.1",
      text: "Descrição do Controle de Ectoparasitas",
      answer: [{ valueString: formData.ectoparasiteControlDescription }],
    });
  }

  // Sempre inclui o grupo principal, mesmo sem subitens
  items.push({
    linkId: "1",
    text: "Anamnese e Histórico Clínico",
    item: anamneseItems,
  });

  // =========================================================================
  // Grupo 2: Observações - Sistemas Corporais
  // =========================================================================
  const observationsItems: QuestionnaireResponseItem[] = [];

  // Cardiorrespiratório
  const cardioItems: QuestionnaireResponseItem[] = [];
  if (formData.cardiorespiratory && formData.cardiorespiratory.length > 0) {
    cardioItems.push({
      linkId: "2.1.1",
      text: "Padrão Respiratório",
      answer: formData.cardiorespiratory.map((value) => ({
        valueString: value,
      })),
    });
  }
  if (formData.cardiorespiratoryOther) {
    cardioItems.push({
      linkId: "2.1.2",
      text: "Outras Observações Cardiorrespiratórias",
      answer: [{ valueString: formData.cardiorespiratoryOther }],
    });
  }
  // Sempre inclui o grupo intermediário, mesmo sem subitens
  observationsItems.push({
    linkId: "2.1",
    text: "Cardiorrespiratório",
    item: cardioItems,
  });

  // Ingestão de Água
  if (formData.waterIntake) {
    observationsItems.push({
      linkId: "2.2",
      text: "Ingestão de Água",
      answer: [{ valueString: formData.waterIntake }],
    });
  }

  // Digestório
  const digestoryItems: QuestionnaireResponseItem[] = [];
  if (formData.digestory && formData.digestory.length > 0) {
    digestoryItems.push({
      linkId: "2.3.1",
      text: "Padrão Alimentar",
      answer: formData.digestory.map((value) => ({ valueString: value })),
    });
  }
  if (formData.digestoryOther) {
    digestoryItems.push({
      linkId: "2.3.2",
      text: "Outras Observações Digestórias",
      answer: [{ valueString: formData.digestoryOther }],
    });
  }
  // Sempre inclui o grupo intermediário, mesmo sem subitens
  observationsItems.push({
    linkId: "2.3",
    text: "Digestório",
    item: digestoryItems,
  });

  // Defecação/Fezes
  const defecationItems: QuestionnaireResponseItem[] = [];
  if (formData.defecation && formData.defecation.length > 0) {
    defecationItems.push({
      linkId: "2.4.1",
      text: "Padrão de Defecação",
      answer: formData.defecation.map((value) => ({ valueString: value })),
    });
  }
  if (formData.defecationOther) {
    defecationItems.push({
      linkId: "2.4.2",
      text: "Outras Observações sobre Fezes",
      answer: [{ valueString: formData.defecationOther }],
    });
  }
  // Sempre inclui o grupo intermediário, mesmo sem subitens
  observationsItems.push({
    linkId: "2.4",
    text: "Defecação/Fezes",
    item: defecationItems,
  });

  // Urogenital
  const urogenitalItems: QuestionnaireResponseItem[] = [];
  if (formData.urogenital) {
    urogenitalItems.push({
      linkId: "2.5.1",
      text: "Presença de Secreção",
      answer: [{ valueString: formData.urogenital }],
    });
  }
  if (formData.urogenitalDescription) {
    urogenitalItems.push({
      linkId: "2.5.2",
      text: "Descrição Urogenital",
      answer: [{ valueString: formData.urogenitalDescription }],
    });
  }
  // Sempre inclui o grupo intermediário, mesmo sem subitens
  observationsItems.push({
    linkId: "2.5",
    text: "Urogenital",
    item: urogenitalItems,
  });

  // Alimentação - Tipo
  const feedingTypeItems: QuestionnaireResponseItem[] = [];
  if (formData.feedingType && formData.feedingType.length > 0) {
    feedingTypeItems.push({
      linkId: "2.6.1",
      text: "Tipos de Alimento",
      answer: formData.feedingType.map((value) => ({ valueString: value })),
    });
  }
  if (formData.feedingTypeDescription) {
    feedingTypeItems.push({
      linkId: "2.6.2",
      text: "Descrição da Alimentação",
      answer: [{ valueString: formData.feedingTypeDescription }],
    });
  }
  // Sempre inclui o grupo intermediário, mesmo sem subitens
  observationsItems.push({
    linkId: "2.6",
    text: "Alimentação - Tipo",
    item: feedingTypeItems,
  });

  // Neurológico
  if (formData.neurological && formData.neurological.length > 0) {
    observationsItems.push({
      linkId: "2.7",
      text: "Neurológico",
      answer: formData.neurological.map((value) => ({ valueString: value })),
    });
  }

  // Intolerância ao Exercício
  if (formData.exerciseIntolerance && formData.exerciseIntolerance.length > 0) {
    observationsItems.push({
      linkId: "2.8",
      text: "Intolerância ao Exercício",
      answer: formData.exerciseIntolerance.map((value) => ({
        valueString: value,
      })),
    });
  }

  // Fêmeas
  if (formData.females && formData.females.length > 0) {
    observationsItems.push({
      linkId: "2.9",
      text: "Fêmeas",
      answer: formData.females.map((value) => ({ valueString: value })),
    });
  }

  // Vermifugação
  const dewormingItems: QuestionnaireResponseItem[] = [];
  if (formData.deworming) {
    dewormingItems.push({
      linkId: "2.10.1",
      text: "Vermifugado",
      answer: [{ valueString: formData.deworming }],
    });
  }
  if (formData.dewormingDescription) {
    dewormingItems.push({
      linkId: "2.10.2",
      text: "Descrição da Vermifugação",
      answer: [{ valueString: formData.dewormingDescription }],
    });
  }
  // Sempre inclui o grupo intermediário, mesmo sem subitens
  observationsItems.push({
    linkId: "2.10",
    text: "Vermifugação",
    item: dewormingItems,
  });

  // Observações Gerais
  if (formData.anamnesisObservations) {
    observationsItems.push({
      linkId: "2.11",
      text: "Observações Gerais da Anamnese",
      answer: [{ valueString: formData.anamnesisObservations }],
    });
  }

  // Sempre inclui o grupo principal, mesmo sem subitens
  items.push({
    linkId: "2",
    text: "Observações - Sistemas Corporais",
    item: observationsItems,
  });

  // =========================================================================
  // Grupo 3: Exame Físico
  // =========================================================================
  const physicalExamItems: QuestionnaireResponseItem[] = [];

  if (formData.physicalExam) {
    physicalExamItems.push({
      linkId: "3.1",
      text: "Exame Físico Geral",
      answer: [{ valueString: formData.physicalExam }],
    });
  }

  if (formData.lymphNodes) {
    physicalExamItems.push({
      linkId: "3.2",
      text: "Linfonodos (palpação)",
      answer: [{ valueString: formData.lymphNodes }],
    });
  }

  if (formData.cardiacPulmonaryAuscultation) {
    physicalExamItems.push({
      linkId: "3.3",
      text: "Ausculta Cardíaca e Pulmonar",
      answer: [{ valueString: formData.cardiacPulmonaryAuscultation }],
    });
  }

  if (formData.eyesMucosa) {
    physicalExamItems.push({
      linkId: "3.4",
      text: "Olhos e Mucosa Ocular",
      answer: [{ valueString: formData.eyesMucosa }],
    });
  }

  if (formData.oralMucosa) {
    physicalExamItems.push({
      linkId: "3.5",
      text: "Boca e Mucosa Oral",
      answer: [{ valueString: formData.oralMucosa }],
    });
  }

  if (formData.earCanals) {
    physicalExamItems.push({
      linkId: "3.6",
      text: "Condutos Auditivos",
      answer: [{ valueString: formData.earCanals }],
    });
  }

  // Pele e Pelagem
  const skinItems: QuestionnaireResponseItem[] = [];
  if (formData.skinConditions && formData.skinConditions.length > 0) {
    skinItems.push({
      linkId: "3.7.1",
      text: "Condições Dermatológicas",
      answer: formData.skinConditions.map((value) => ({ valueString: value })),
    });
  }
  if (formData.skin) {
    skinItems.push({
      linkId: "3.7.2",
      text: "Descrição da Pele",
      answer: [{ valueString: formData.skin }],
    });
  }
  if (formData.skinOther) {
    skinItems.push({
      linkId: "3.7.3",
      text: "Outras Observações sobre Pele",
      answer: [{ valueString: formData.skinOther }],
    });
  }
  // Sempre inclui o grupo intermediário, mesmo sem subitens
  physicalExamItems.push({
    linkId: "3.7",
    text: "Pele e Pelagem",
    item: skinItems,
  });

  if (formData.locomotorSystem) {
    physicalExamItems.push({
      linkId: "3.8",
      text: "Aparelho Locomotor",
      answer: [{ valueString: formData.locomotorSystem }],
    });
  }

  if (formData.anteriorLimbs) {
    physicalExamItems.push({
      linkId: "3.9",
      text: "Membros Anteriores",
      answer: [{ valueString: formData.anteriorLimbs }],
    });
  }

  if (formData.posteriorLimbs) {
    physicalExamItems.push({
      linkId: "3.10",
      text: "Membros Posteriores",
      answer: [{ valueString: formData.posteriorLimbs }],
    });
  }

  // Sempre inclui o grupo principal, mesmo sem subitens
  items.push({
    linkId: "3",
    text: "Exame Físico",
    item: physicalExamItems,
  });

  // =========================================================================
  // Grupo 4: Sinais Vitais
  // =========================================================================
  const vitalSignsItems: QuestionnaireResponseItem[] = [];

  if (formData.heartRate !== undefined) {
    vitalSignsItems.push({
      linkId: "4.1",
      text: "Frequência Cardíaca (FC)",
      answer: [{ valueInteger: formData.heartRate }],
    });
  }

  if (formData.respiratoryRate !== undefined) {
    vitalSignsItems.push({
      linkId: "4.2",
      text: "Frequência Respiratória (FR)",
      answer: [{ valueInteger: formData.respiratoryRate }],
    });
  }

  if (formData.capillaryRefillTime !== undefined) {
    vitalSignsItems.push({
      linkId: "4.3",
      text: "Tempo de Preenchimento Capilar (TPC)",
      answer: [{ valueDecimal: formData.capillaryRefillTime }],
    });
  }

  if (formData.hydration) {
    vitalSignsItems.push({
      linkId: "4.4",
      text: "Hidratação",
      answer: [{ valueString: formData.hydration }],
    });
  }

  if (formData.temperature !== undefined) {
    vitalSignsItems.push({
      linkId: "4.5",
      text: "Temperatura",
      answer: [{ valueDecimal: formData.temperature }],
    });
  }

  if (formData.weight !== undefined) {
    vitalSignsItems.push({
      linkId: "4.6",
      text: "Peso",
      answer: [{ valueDecimal: formData.weight }],
    });
  }

  // Sempre inclui o grupo principal, mesmo sem subitens
  items.push({
    linkId: "4",
    text: "Sinais Vitais",
    item: vitalSignsItems,
  });

  // =========================================================================
  // Grupo 5: Diagnósticos e Condutas
  // =========================================================================
  const diagnosisItems: QuestionnaireResponseItem[] = [];

  // Suspeitas Diagnósticas (múltiplas)
  if (
    formData.diagnosticSuspicions &&
    formData.diagnosticSuspicions.length > 0
  ) {
    formData.diagnosticSuspicions.forEach((suspicion, index) => {
      const suspicionItems: QuestionnaireResponseItem[] = [
        {
          linkId: "5.1.1",
          text: "ID da Suspeita",
          answer: [{ valueString: suspicion.id }],
        },
        {
          linkId: "5.1.2",
          text: "Descrição da Suspeita Diagnóstica",
          answer: [{ valueString: suspicion.description }],
        },
      ];

      // Adiciona status apenas se existir
      if (suspicion.status) {
        suspicionItems.push({
          linkId: "5.1.3",
          text: "Status da Suspeita",
          answer: [
            {
              valueString: suspicion.status,
            },
          ],
        });
      }

      // Adiciona descrição de confirmação sempre que existir
      if (suspicion.confirmationDescription) {
        suspicionItems.push({
          linkId: "5.1.4",
          text: "Descrição da Confirmação",
          answer: [{ valueString: suspicion.confirmationDescription }],
        });
      }

      diagnosisItems.push({
        linkId: `5.1.${index + 1}`,
        text: `Suspeita Diagnóstica ${index + 1}`,
        item: suspicionItems,
      });
    });
  }

  // Condutas Clínicas (múltiplas)
  if (formData.clinicalConducts && formData.clinicalConducts.length > 0) {
    formData.clinicalConducts.forEach((conduct, index) => {
      const conductItems: QuestionnaireResponseItem[] = [
        {
          linkId: "5.2.1",
          text: "ID da Conduta",
          answer: [{ valueString: conduct.id }],
        },
        {
          linkId: "5.2.2",
          text: "Descrição da Conduta Clínica",
          answer: [{ valueString: conduct.description }],
        },
      ];

      // Adiciona status apenas se existir
      if (conduct.status) {
        conductItems.push({
          linkId: "5.2.3",
          text: "Status da Conduta",
          answer: [
            {
              valueString: conduct.status,
            },
          ],
        });
      }

      // Adiciona descrição do resultado sempre que existir
      if (conduct.outcomeDescription) {
        conductItems.push({
          linkId: "5.2.4",
          text: "Descrição do Resultado/Desfecho",
          answer: [{ valueString: conduct.outcomeDescription }],
        });
      }

      diagnosisItems.push({
        linkId: `5.2.${index + 1}`,
        text: `Conduta Clínica ${index + 1}`,
        item: conductItems,
      });
    });
  }

  // Sempre inclui o grupo principal, mesmo sem subitens
  items.push({
    linkId: "5",
    text: "Diagnósticos e Condutas",
    item: diagnosisItems,
  });

  // =========================================================================
  // Grupo 6: Observações Adicionais
  // =========================================================================
  // Sempre inclui o grupo principal, mesmo sem valor
  items.push({
    linkId: "6",
    text: "Observações Adicionais",
    ...(formData.observations && {
      answer: [{ valueString: formData.observations }],
    }),
  });

  // =========================================================================
  // Grupo 7: Prescrições - Tratamentos
  // =========================================================================
  // Sempre inclui o grupo principal, processa tratamentos se existirem
  if (formData.treatments && formData.treatments.length > 0) {
    formData.treatments.forEach((treatment) => {
      const treatmentItems: QuestionnaireResponseItem[] = [
        {
          linkId: "7.1",
          text: "ID do Tratamento",
          answer: [{ valueString: treatment.id }],
        },
        {
          linkId: "7.2",
          text: "Nome do Medicamento",
          answer: [{ valueString: treatment.name }],
        },
        {
          linkId: "7.3",
          text: "Dosagem",
          answer: [{ valueString: treatment.dosage }],
        },
        {
          linkId: "7.4",
          text: "Descrição do Tratamento",
          answer: [{ valueString: treatment.description }],
        },
      ];

      if (treatment.signed !== undefined) {
        treatmentItems.push({
          linkId: "7.5",
          text: "Assinado Digitalmente",
          answer: [{ valueBoolean: treatment.signed }],
        });
      }

      items.push({
        linkId: "7",
        text: "Prescrições - Tratamentos",
        item: treatmentItems,
      });
    });
  } else {
    // Inclui o grupo mesmo sem tratamentos
    items.push({
      linkId: "7",
      text: "Prescrições - Tratamentos",
      item: [],
    });
  }

  // =========================================================================
  // Grupo 8: Prescrições - Exames ou Procedimentos
  // =========================================================================
  // Sempre inclui o grupo principal, processa exames se existirem
  if (formData.examsOrProcedures && formData.examsOrProcedures.length > 0) {
    formData.examsOrProcedures.forEach((exam) => {
      const examItems: QuestionnaireResponseItem[] = [
        {
          linkId: "8.1",
          text: "ID do Exame/Procedimento",
          answer: [{ valueString: exam.id }],
        },
        {
          linkId: "8.2",
          text: "Descrição do Exame/Procedimento",
          answer: [{ valueString: exam.description }],
        },
        {
          linkId: "8.3",
          text: "Notas Adicionais",
          answer: [{ valueString: exam.notes }],
        },
      ];

      if (exam.signed !== undefined) {
        examItems.push({
          linkId: "8.4",
          text: "Assinado Digitalmente",
          answer: [{ valueBoolean: exam.signed }],
        });
      }

      items.push({
        linkId: "8",
        text: "Prescrições - Exames ou Procedimentos",
        item: examItems,
      });
    });
  } else {
    // Inclui o grupo mesmo sem exames
    items.push({
      linkId: "8",
      text: "Prescrições - Exames ou Procedimentos",
      item: [],
    });
  }

  // =========================================================================
  // Grupo 9: Prescrições - Relatórios
  // =========================================================================
  // Sempre inclui o grupo principal, processa relatórios se existirem
  if (formData.reports && formData.reports.length > 0) {
    formData.reports.forEach((report) => {
      const reportItems: QuestionnaireResponseItem[] = [
        {
          linkId: "9.1",
          text: "ID do Relatório",
          answer: [{ valueString: report.id }],
        },
        {
          linkId: "9.2",
          text: "Conteúdo do Relatório",
          answer: [{ valueString: report.content }],
        },
      ];

      if (report.signed !== undefined) {
        reportItems.push({
          linkId: "9.3",
          text: "Assinado Digitalmente",
          answer: [{ valueBoolean: report.signed }],
        });
      }

      items.push({
        linkId: "9",
        text: "Prescrições - Relatórios",
        item: reportItems,
      });
    });
  } else {
    // Inclui o grupo mesmo sem relatórios
    items.push({
      linkId: "9",
      text: "Prescrições - Relatórios",
      item: [],
    });
  }

  // =========================================================================
  // Grupo 10: Orientações ao Tutor
  // =========================================================================
  // Sempre inclui o grupo principal, mesmo sem valor
  items.push({
    linkId: "10",
    text: "Orientações ao Tutor",
    ...(formData.guidelines && {
      answer: [{ valueString: formData.guidelines }],
    }),
  });

  // =========================================================================
  // Grupo 11: Documentos Assinados Digitalmente
  // =========================================================================
  // Sempre inclui o grupo principal, processa documentos se existirem
  if (formData.signedDocuments && formData.signedDocuments.length > 0) {
    formData.signedDocuments.forEach((doc) => {
      const docItems: QuestionnaireResponseItem[] = [
        {
          linkId: "11.1",
          text: "UUID do Documento",
          answer: [{ valueString: doc.uuid }],
        },
        {
          linkId: "11.2",
          text: "Título do Documento",
          answer: [{ valueString: doc.title }],
        },
        {
          linkId: "11.3",
          text: "Nome do Arquivo",
          answer: [{ valueString: doc.filename }],
        },
        {
          linkId: "11.4",
          text: "Documento Base64",
          answer: [{ valueString: doc.base64 }],
        },
        {
          linkId: "11.5",
          text: "Data/Hora da Assinatura",
          answer: [{ valueDateTime: doc.signedAt }],
        },
        {
          linkId: "11.6",
          text: "Tipo de Documento",
          answer: [{ valueString: doc.type }],
        },
      ];

      if (doc.itemIds && doc.itemIds.length > 0) {
        docItems.push({
          linkId: "11.7",
          text: "IDs dos Itens Incluídos",
          answer: doc.itemIds.map((id) => ({ valueString: id })),
        });
      }

      items.push({
        linkId: "11",
        text: "Documentos Assinados Digitalmente",
        item: docItems,
      });
    });
  } else {
    // Inclui o grupo mesmo sem documentos
    items.push({
      linkId: "11",
      text: "Documentos Assinados Digitalmente",
      item: [],
    });
  }

  // =========================================================================
  // Grupo 12: Questionários selecionados (envio via tasks ao finalizar)
  // =========================================================================
  if (formData.selectedQuestionnaires && formData.selectedQuestionnaires.length > 0) {
    formData.selectedQuestionnaires.forEach(
      (q: NonNullable<IVetAttendanceForm["selectedQuestionnaires"]>[number]) => {
        items.push({
          linkId: "12",
          text: "Questionários selecionados",
          item: [
            {
              linkId: "12.1",
              text: "ID do Questionnaire",
              answer: [{ valueString: q.questionnaireId }],
            },
            {
              linkId: "12.2",
              text: "Título",
              answer: [{ valueString: q.title }],
            },
            {
              linkId: "12.3",
              text: "Nome",
              answer: [{ valueString: q.name }],
            },
            {
              linkId: "12.4",
              text: "Label",
              answer: [{ valueString: q.label }],
            },
            ...(q.usageType
              ? [
                  {
                    linkId: "12.5",
                    text: "Tipo de uso",
                    answer: [{ valueString: q.usageType }],
                  },
                ]
              : []),
          ],
        });
      },
    );
  } else {
    items.push({
      linkId: "12",
      text: "Questionários selecionados",
      item: [],
    });
  }

  // =========================================================================
  // Construir e retornar o QuestionnaireResponse
  // =========================================================================
  const questionnaireResponse: QuestionnaireResponse = {
    resourceType: "QuestionnaireResponse",
    questionnaire: "http://fluxmed.com.br/fhir/Questionnaire/vet-attendance",
    status: "completed",
    subject: {
      reference: `Patient/${patientReference}`,
    },
    authored: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
    item: items,
  };

  if (practitionerReference) {
    questionnaireResponse.author = {
      reference: practitionerReference,
      display: practitionerDisplay,
    };
  }

  if (encounterId) {
    questionnaireResponse.encounter = {
      reference: `Encounter/${encounterId}`,
    };
  }

  return questionnaireResponse;
}

/**
 * Função auxiliar para buscar um item pelo linkId recursivamente
 */
function findItemByLinkId(
  items: QuestionnaireResponseItem[] | undefined,
  linkId: string
): QuestionnaireResponseItem | undefined {
  if (!items) return undefined;

  for (const item of items) {
    if (item.linkId === linkId) {
      return item;
    }
    if (item.item) {
      const found = findItemByLinkId(item.item, linkId);
      if (found) return found;
    }
  }

  return undefined;
}

/**
 * Extrai o valor de um campo específico do QuestionnaireResponse
 */
function extractValue(
  questionnaireResponse: QuestionnaireResponse,
  linkId: string
): any {
  const item = findItemByLinkId(questionnaireResponse.item, linkId);
  if (!item || !item.answer || item.answer.length === 0) {
    return undefined;
  }
  const answer = item.answer[0];
  return (
    answer.valueString ??
    answer.valueBoolean ??
    answer.valueInteger ??
    answer.valueDecimal ??
    undefined
  );
}

/**
 * Extrai o valor de um item específico (para uso dentro de grupos)
 */
function extractValueFromItem(
  parentItem: QuestionnaireResponseItem,
  linkId: string
): any {
  const item = findItemByLinkId(parentItem.item, linkId);
  if (!item || !item.answer || item.answer.length === 0) {
    return undefined;
  }
  const answer = item.answer[0];
  return (
    answer.valueString ??
    answer.valueBoolean ??
    answer.valueInteger ??
    answer.valueDecimal ??
    undefined
  );
}

/**
 * Extrai múltiplos valores de um campo (para arrays)
 */
function extractMultipleValues(
  questionnaireResponse: QuestionnaireResponse,
  linkId: string
): string[] {
  const item = findItemByLinkId(questionnaireResponse.item, linkId);
  if (!item || !item.answer || item.answer.length === 0) {
    return [];
  }
  return item.answer
    .map((ans) => ans.valueString)
    .filter((val): val is string => val !== undefined);
}

/**
 * Converte QuestionnaireResponse FHIR R5 para dados do formulário de atendimento
 * Faz o mapeamento reverso do convertToQuestionnaireResponse
 *
 * @param questionnaireResponse - QuestionnaireResponse FHIR R5 a ser convertido
 * @returns Dados do formulário de atendimento
 */
export function convertFromQuestionnaireResponse(
  questionnaireResponse: QuestionnaireResponse
): Partial<IVetAttendanceForm> {
  const formData: Partial<IVetAttendanceForm> = {};

  // =========================================================================
  // Grupo 1: Anamnese e Histórico Clínico
  // =========================================================================
  formData.mainComplaint = extractValue(questionnaireResponse, "1.1") || "";
  formData.anamnesis = extractValue(questionnaireResponse, "1.2");
  formData.continuousMedication = extractValue(questionnaireResponse, "1.3");
  formData.recentExams = extractValue(questionnaireResponse, "1.4");
  formData.streetAccess = extractValue(questionnaireResponse, "1.5");
  formData.streetAccessDescription = extractValue(
    questionnaireResponse,
    "1.5.1"
  );
  formData.feeding = extractValue(questionnaireResponse, "1.6");
  formData.vaccination = extractValue(questionnaireResponse, "1.7");
  formData.vaccinationDescription = extractValue(
    questionnaireResponse,
    "1.7.1"
  );
  formData.ectoparasiteControl = extractValue(questionnaireResponse, "1.8");
  formData.ectoparasiteControlDescription = extractValue(
    questionnaireResponse,
    "1.8.1"
  );

  // =========================================================================
  // Grupo 2: Observações - Sistemas Corporais
  // =========================================================================
  formData.cardiorespiratory = extractMultipleValues(
    questionnaireResponse,
    "2.1.1"
  );
  formData.cardiorespiratoryOther = extractValue(
    questionnaireResponse,
    "2.1.2"
  );
  formData.waterIntake = extractValue(questionnaireResponse, "2.2");
  formData.digestory = extractMultipleValues(questionnaireResponse, "2.3.1");
  formData.digestoryOther = extractValue(questionnaireResponse, "2.3.2");
  formData.defecation = extractMultipleValues(questionnaireResponse, "2.4.1");
  formData.defecationOther = extractValue(questionnaireResponse, "2.4.2");
  formData.urogenital = extractValue(questionnaireResponse, "2.5.1");
  formData.urogenitalDescription = extractValue(questionnaireResponse, "2.5.2");
  formData.feedingType = extractMultipleValues(questionnaireResponse, "2.6.1");
  formData.feedingTypeDescription = extractValue(
    questionnaireResponse,
    "2.6.2"
  );
  formData.neurological = extractMultipleValues(questionnaireResponse, "2.7");
  formData.exerciseIntolerance = extractMultipleValues(
    questionnaireResponse,
    "2.8"
  );
  formData.females = extractMultipleValues(questionnaireResponse, "2.9");
  formData.deworming = extractValue(questionnaireResponse, "2.10.1");
  formData.dewormingDescription = extractValue(questionnaireResponse, "2.10.2");
  formData.anamnesisObservations = extractValue(questionnaireResponse, "2.11");

  // =========================================================================
  // Grupo 3: Exame Físico
  // =========================================================================
  formData.physicalExam = extractValue(questionnaireResponse, "3.1");
  formData.lymphNodes = extractValue(questionnaireResponse, "3.2");
  formData.cardiacPulmonaryAuscultation = extractValue(
    questionnaireResponse,
    "3.3"
  );
  formData.eyesMucosa = extractValue(questionnaireResponse, "3.4");
  formData.oralMucosa = extractValue(questionnaireResponse, "3.5");
  formData.earCanals = extractValue(questionnaireResponse, "3.6");
  formData.skinConditions = extractMultipleValues(
    questionnaireResponse,
    "3.7.1"
  );
  formData.skin = extractValue(questionnaireResponse, "3.7.2");
  formData.skinOther = extractValue(questionnaireResponse, "3.7.3");
  formData.locomotorSystem = extractValue(questionnaireResponse, "3.8");
  formData.anteriorLimbs = extractValue(questionnaireResponse, "3.9");
  formData.posteriorLimbs = extractValue(questionnaireResponse, "3.10");

  // =========================================================================
  // Grupo 4: Sinais Vitais
  // =========================================================================
  formData.heartRate = extractValue(questionnaireResponse, "4.1");
  formData.respiratoryRate = extractValue(questionnaireResponse, "4.2");
  formData.capillaryRefillTime = extractValue(questionnaireResponse, "4.3");
  formData.hydration = extractValue(questionnaireResponse, "4.4");
  formData.temperature = extractValue(questionnaireResponse, "4.5");
  formData.weight = extractValue(questionnaireResponse, "4.6");

  // =========================================================================
  // Grupo 5: Diagnósticos e Condutas
  // =========================================================================
  const group5 = findItemByLinkId(questionnaireResponse.item, "5");
  if (group5 && group5.item) {
    // Suspeitas Diagnósticas (5.1.x)
    const suspicions: Array<{
      id: string;
      description: string;
      status?: "confirmed" | "not_confirmed";
      confirmationDescription?: string;
    }> = [];

    group5.item.forEach((item) => {
      if (item.linkId.startsWith("5.1.") && item.item) {
        const idItem = item.item.find((subItem) => subItem.linkId === "5.1.1");
        const descriptionItem = item.item.find(
          (subItem) => subItem.linkId === "5.1.2"
        );
        const statusItem = item.item.find(
          (subItem) => subItem.linkId === "5.1.3"
        );
        const confirmationItem = item.item.find(
          (subItem) => subItem.linkId === "5.1.4"
        );

        const id = idItem?.answer?.[0]?.valueString || "";
        const description = descriptionItem?.answer?.[0]?.valueString || "";
        const status = statusItem?.answer?.[0]?.valueString as
          | "confirmed"
          | "not_confirmed"
          | undefined;
        const confirmationDescription =
          confirmationItem?.answer?.[0]?.valueString;

        if (id && description) {
          suspicions.push({
            id,
            description,
            ...(status && { status }),
            ...(confirmationDescription && { confirmationDescription }),
          });
        }
      }
    });

    if (suspicions.length > 0) {
      formData.diagnosticSuspicions = suspicions;
    }

    // Condutas Clínicas (5.2.x)
    const conducts: Array<{
      id: string;
      description: string;
      status?: "success" | "failed" | "in_progress";
      outcomeDescription?: string;
    }> = [];

    group5.item.forEach((item) => {
      if (item.linkId.startsWith("5.2.") && item.item) {
        const idItem = item.item.find((subItem) => subItem.linkId === "5.2.1");
        const descriptionItem = item.item.find(
          (subItem) => subItem.linkId === "5.2.2"
        );
        const statusItem = item.item.find(
          (subItem) => subItem.linkId === "5.2.3"
        );
        const outcomeItem = item.item.find(
          (subItem) => subItem.linkId === "5.2.4"
        );

        const id = idItem?.answer?.[0]?.valueString || "";
        const description = descriptionItem?.answer?.[0]?.valueString || "";
        const status = statusItem?.answer?.[0]?.valueString as
          | "success"
          | "failed"
          | "in_progress"
          | undefined;
        const outcomeDescription = outcomeItem?.answer?.[0]?.valueString;

        if (id && description) {
          conducts.push({
            id,
            description,
            ...(status && { status }),
            ...(outcomeDescription && { outcomeDescription }),
          });
        }
      }
    });

    if (conducts.length > 0) {
      formData.clinicalConducts = conducts;
    }
  }

  // =========================================================================
  // Grupo 6: Observações Adicionais
  // =========================================================================
  formData.observations = extractValue(questionnaireResponse, "6");

  // =========================================================================
  // Grupo 7: Prescrições - Tratamentos
  // =========================================================================
  const group7Items = questionnaireResponse.item?.filter(
    (item) => item.linkId === "7" && item.item
  );
  if (group7Items && group7Items.length > 0) {
    const treatments: Array<{
      id: string;
      name: string;
      dosage: string;
      description: string;
      signed?: boolean;
    }> = [];

    group7Items.forEach((groupItem) => {
      if (groupItem.item) {
        const id = extractValueFromItem(groupItem, "7.1") || "";
        const name = extractValueFromItem(groupItem, "7.2") || "";
        const dosage = extractValueFromItem(groupItem, "7.3") || "";
        const description = extractValueFromItem(groupItem, "7.4") || "";
        const signed = extractValueFromItem(groupItem, "7.5");

        if (id && name) {
          treatments.push({
            id,
            name,
            dosage,
            description,
            ...(signed !== undefined && { signed }),
          });
        }
      }
    });

    if (treatments.length > 0) {
      formData.treatments = treatments;
    }
  }

  // =========================================================================
  // Grupo 8: Prescrições - Exames ou Procedimentos
  // =========================================================================
  const group8Items = questionnaireResponse.item?.filter(
    (item) => item.linkId === "8" && item.item
  );
  if (group8Items && group8Items.length > 0) {
    const examsOrProcedures: Array<{
      id: string;
      description: string;
      notes: string;
      signed?: boolean;
    }> = [];

    group8Items.forEach((groupItem) => {
      if (groupItem.item) {
        const id = extractValueFromItem(groupItem, "8.1") || "";
        const description = extractValueFromItem(groupItem, "8.2") || "";
        const notes = extractValueFromItem(groupItem, "8.3") || "";
        const signed = extractValueFromItem(groupItem, "8.4");

        if (id && description) {
          examsOrProcedures.push({
            id,
            description,
            notes,
            ...(signed !== undefined && { signed }),
          });
        }
      }
    });

    if (examsOrProcedures.length > 0) {
      formData.examsOrProcedures = examsOrProcedures;
    }
  }

  // =========================================================================
  // Grupo 9: Prescrições - Relatórios
  // =========================================================================
  const group9Items = questionnaireResponse.item?.filter(
    (item) => item.linkId === "9" && item.item
  );
  if (group9Items && group9Items.length > 0) {
    const reports: Array<{
      id: string;
      content: string;
      signed?: boolean;
    }> = [];

    group9Items.forEach((groupItem) => {
      if (groupItem.item) {
        const id = extractValueFromItem(groupItem, "9.1") || "";
        const content = extractValueFromItem(groupItem, "9.2") || "";
        const signed = extractValueFromItem(groupItem, "9.3");

        if (id && content) {
          reports.push({
            id,
            content,
            ...(signed !== undefined && { signed }),
          });
        }
      }
    });

    if (reports.length > 0) {
      formData.reports = reports;
    }
  }

  // =========================================================================
  // Grupo 10: Orientações ao Tutor
  // =========================================================================
  formData.guidelines = extractValue(questionnaireResponse, "10");

  // =========================================================================
  // Grupo 11: Documentos Assinados Digitalmente
  // =========================================================================
  const group11Items = questionnaireResponse.item?.filter(
    (item) => item.linkId === "11" && item.item
  );
  if (group11Items && group11Items.length > 0) {
    const signedDocuments: Array<{
      uuid: string;
      title: string;
      filename: string;
      base64: string;
      signedAt: string;
      type: "treatment" | "exam" | "report" | "combined";
      itemIds: string[];
    }> = [];

    group11Items.forEach((groupItem) => {
      if (groupItem.item) {
        const uuid = extractValueFromItem(groupItem, "11.1") || "";
        const title = extractValueFromItem(groupItem, "11.2") || "";
        const filename = extractValueFromItem(groupItem, "11.3") || "";
        const base64 = extractValueFromItem(groupItem, "11.4") || "";
        const signedAt = extractValueFromItem(groupItem, "11.5") || "";
        const type = extractValueFromItem(groupItem, "11.6") as
          | "treatment"
          | "exam"
          | "report"
          | "combined"
          | undefined;

        const itemIdsItem = findItemByLinkId(groupItem.item, "11.7");
        const itemIds =
          itemIdsItem?.answer
            ?.map((ans) => ans.valueString)
            .filter((val): val is string => val !== undefined) || [];

        if (uuid && title && filename && base64 && signedAt && type) {
          signedDocuments.push({
            uuid,
            title,
            filename,
            base64,
            signedAt,
            type,
            itemIds,
          });
        }
      }
    });

    if (signedDocuments.length > 0) {
      formData.signedDocuments = signedDocuments;
    }
  }

  // =========================================================================
  // Grupo 12: Questionários selecionados
  // =========================================================================
  const group12Items = questionnaireResponse.item?.filter(
    (item) => item.linkId === "12" && item.item
  );
  if (group12Items && group12Items.length > 0) {
    const selectedQuestionnaires: NonNullable<
      IVetAttendanceForm["selectedQuestionnaires"]
    > = [];
    group12Items.forEach((groupItem) => {
      if (groupItem.item) {
        const questionnaireId = extractValueFromItem(groupItem, "12.1") || "";
        const title = extractValueFromItem(groupItem, "12.2") || "";
        const name = extractValueFromItem(groupItem, "12.3") || title;
        const label = extractValueFromItem(groupItem, "12.4") || questionnaireId;
        const usageType = extractValueFromItem(groupItem, "12.5");
        if (questionnaireId) {
          selectedQuestionnaires.push({
            type: "questionnaire",
            questionnaireId,
            title: title || name,
            name: name || title,
            label: label || questionnaireId,
            ...(usageType !== undefined && usageType !== ""
              ? { usageType: String(usageType) }
              : {}),
          });
        }
      }
    });
    if (selectedQuestionnaires.length > 0) {
      formData.selectedQuestionnaires = selectedQuestionnaires;
    }
  }

  return formData;
}

/**
 * Exemplo de uso:
 *
 * const formData = methods.getValues();
 * const questionnaireResponse = convertToQuestionnaireResponse(
 *   formData,
 *   "Patient/NAL16C1481-DF",
 *   "Practitioner/veterinario-123",
 *   "encounter-456"
 * );
 *
 * // Enviar para API FHIR
 * await fhirClient.create(questionnaireResponse);
 *
 * // Para editar, converter de volta:
 * const formDataFromResponse = convertFromQuestionnaireResponse(questionnaireResponse);
 * methods.reset(formDataFromResponse);
 */
